import React from 'react';
import { X, Download, Send, MapPin, User, Calendar, DollarSign, FileText, Building } from 'lucide-react';
import { invoiceAPI } from "../../services/api";
import { toast } from "react-toastify";

const AddressDisplay = ({ address, title }) => {
  if (!address) return null;
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
        <MapPin size={16} />
        {title}
      </h4>
      <div className="text-sm text-gray-600 space-y-1">
        {address.streetLine1 && <p>{address.streetLine1}</p>}
        {address.streetLine2 && <p>{address.streetLine2}</p>}
        <p>
          {[address.city, address.state, address.zipcode]
            .filter(Boolean)
            .join(', ')}
        </p>
        {address.country && <p>{address.country}</p>}
      </div>
    </div>
  );
};

const InvoicePreview = ({ invoice, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleDownloadDocument = async (fileUrl) => {
    try {
      const response = await invoiceAPI.getDocumentDownloadUrl(fileUrl);
      const downloadUrl = response.data.downloadUrl;
      window.open(downloadUrl, '_blank');
      toast.success('Document download started');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await invoiceAPI.getPlacementOrderPdf(invoice.id);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber || invoice.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleResendEmail = async () => {
    try {
      await invoiceAPI.resendInvoiceEmail(invoice.id);
      toast.success('Invoice email sent successfully');
    } catch (error) {
      console.error('Error sending email:', error);
      const errMsg = error.response?.data?.message || '';
      if (errMsg.includes('No email address')) {
        toast.error('Client email is missing. Please edit the invoice and add the client email before sending.');
      } else {
        toast.error(errMsg || 'Failed to send invoice email');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm">
      {/* Overlay - click to close */}
      <div className="absolute inset-0" onClick={onClose}></div>
      
      {/* Right Side Panel */}
      <div className="relative bg-white w-full max-w-3xl h-full flex flex-col shadow-2xl animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold">Placement Order Invoice</h2>
            <p className="text-sm text-blue-100 mt-1">
              {invoice.candidateName} • {invoice.designation}
            </p>
            {invoice.invoiceStatus && (
              <span className={`inline-block mt-2 px-3 py-1 text-xs font-semibold rounded-full ${
                invoice.invoiceStatus === 'PAID' ? 'bg-green-100 text-green-800' :
                invoice.invoiceStatus === 'OVERDUE' ? 'bg-red-100 text-red-800' :
                invoice.invoiceStatus === 'SENT' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-700'
              }`}>
                {invoice.invoiceStatus}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadPdf}
              className="px-4 py-2 bg-white text-blue-600 rounded-md hover:bg-blue-50 flex items-center gap-2 text-sm font-medium"
            >
              <Download size={16} />
              Download PDF
            </button>
            <button
              onClick={handleResendEmail}
              className="px-4 py-2 bg-white text-blue-600 rounded-md hover:bg-blue-50 flex items-center gap-2 text-sm font-medium"
            >
              <Send size={16} />
              Send Email
            </button>
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-blue-500 rounded-full"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <DollarSign size={20} />
                <span className="text-xs font-medium uppercase">CTC</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(invoice.ctc)}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 text-green-600 mb-2">
                <DollarSign size={20} />
                <span className="text-xs font-medium uppercase">Fee</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(invoice.calculatedFee)}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 text-purple-600 mb-2">
                <Calendar size={20} />
                <span className="text-xs font-medium uppercase">Joining</span>
              </div>
              <p className="text-sm font-bold text-gray-900">{formatDate(invoice.joiningDate)}</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 text-orange-600 mb-2">
                <FileText size={20} />
                <span className="text-xs font-medium uppercase">Type</span>
              </div>
              <p className="text-sm font-bold text-gray-900">{invoice.placementType}</p>
            </div>
          </div>

          {/* Client Details */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Building size={20} className="text-blue-600" />
              Client Information
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Client Name</label>
                <p className="text-sm font-semibold text-gray-900">{invoice.clientName || 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Email</label>
                <p className="text-sm text-gray-900">{invoice.clientEmail || 'N/A'}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">PO Number</label>
                  <p className="text-sm text-gray-900">{invoice.poNumber || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">GSTIN</label>
                  <p className="text-sm text-gray-900">{invoice.clientGSTIN || 'N/A'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">GST Status</label>
                  <p className="text-sm text-gray-900">{invoice.gstStatus || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Payment Terms</label>
                  <p className="text-sm text-gray-900">{invoice.paymentTerms || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Addresses */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 space-y-4">
            <AddressDisplay address={invoice.billingAddress} title="Billing Address" />
            {!invoice.sameAsBilling && invoice.shippingAddress && (
              <AddressDisplay address={invoice.shippingAddress} title="Shipping Address" />
            )}
          </div>

          {/* Candidate Details */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <User size={20} className="text-blue-600" />
              Candidate Details
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Candidate Name</label>
                <p className="text-sm font-semibold text-gray-900">{invoice.candidateName || 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Designation</label>
                <p className="text-sm text-gray-900">{invoice.designation || 'N/A'}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Placement Type</label>
                  <p className="text-sm text-gray-900">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                      {invoice.placementType}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">CTC</label>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(invoice.ctc)}</p>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Joining Date</label>
                <p className="text-sm text-gray-900">{formatDate(invoice.joiningDate)}</p>
              </div>
            </div>
          </div>

          {/* Fee Structure */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign size={20} className="text-green-600" />
              Fee Structure
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Fee Model</label>
                <p className="text-sm font-semibold text-gray-900">{invoice.feeModel || 'N/A'}</p>
              </div>
              {invoice.feePercentage && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Fee Percentage</label>
                  <p className="text-sm text-gray-900">{invoice.feePercentage}%</p>
                </div>
              )}
              {invoice.fixedAmount && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Fixed Amount</label>
                  <p className="text-sm text-gray-900">{formatCurrency(invoice.fixedAmount)}</p>
                </div>
              )}
              <div className="pt-3 border-t border-gray-300">
                <label className="text-xs font-medium text-gray-500">Calculated Fee</label>
                <p className="text-lg font-bold text-green-600">{formatCurrency(invoice.calculatedFee)}</p>
              </div>
              {invoice.invoiceNumber && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Invoice Number</label>
                  <p className="text-sm font-semibold text-gray-900">{invoice.invoiceNumber}</p>
                </div>
              )}
              {invoice.invoiceDate && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Invoice Date</label>
                  <p className="text-sm text-gray-900">{formatDateShort(invoice.invoiceDate)}</p>
                </div>
              )}
              {invoice.placeOfSupply && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Place of Supply</label>
                  <p className="text-sm text-gray-900">{invoice.placeOfSupply}</p>
                </div>
              )}
              {invoice.gstType === 'IGST' && (
                <div>
                  <label className="text-xs font-medium text-gray-500">IGST @ 18%</label>
                  <p className="text-sm text-gray-900">{formatCurrency(invoice.igstAmount)}</p>
                </div>
              )}
              {invoice.gstType === 'CGST_SGST' && (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-500">CGST @ 9%</label>
                    <p className="text-sm text-gray-900">{formatCurrency(invoice.cgstAmount)}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">SGST @ 9%</label>
                    <p className="text-sm text-gray-900">{formatCurrency(invoice.sgstAmount)}</p>
                  </div>
                </>
              )}
              {invoice.tdsAmount > 0 && (
                <div>
                  <label className="text-xs font-medium text-gray-500">TDS Deducted</label>
                  <p className="text-sm font-semibold text-red-600">-{formatCurrency(invoice.tdsAmount)}</p>
                </div>
              )}
              <div className="pt-3 border-t border-gray-300">
                <label className="text-xs font-medium text-gray-500">Net Payable</label>
                <p className="text-lg font-bold text-blue-600">{formatCurrency(invoice.netPayable)}</p>
              </div>
              {invoice.authorisedSignatoryName && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Authorised Signatory</label>
                  <p className="text-sm font-semibold text-gray-900">{invoice.authorisedSignatoryName}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Guarantee Days</label>
                  <p className="text-sm text-gray-900">{invoice.guaranteeDays || 'N/A'} days</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Guarantee End</label>
                  <p className="text-sm text-gray-900">{formatDate(invoice.guaranteeEndDate)}</p>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">If Candidate Leaves</label>
                <p className="text-sm text-gray-900">{invoice.ifCandidateLeaves || 'N/A'}</p>
              </div>
              {invoice.exitPenalty && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Exit Penalty</label>
                  <p className="text-sm text-gray-900">{invoice.exitPenalty}</p>
                </div>
              )}
            </div>
          </div>

          {/* Company Details */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Your Company Details</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">GSTIN</label>
                    <p className="text-sm text-gray-900">{invoice.yourGSTIN || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">PAN</label>
                    <p className="text-sm text-gray-900">{invoice.yourPan || 'N/A'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Account Number</label>
                    <p className="text-sm text-gray-900">{invoice.accountNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">IFSC Code</label>
                    <p className="text-sm text-gray-900">{invoice.ifscCode || 'N/A'}</p>
                  </div>
                </div>
              </div>
              <div>
                <AddressDisplay address={invoice.fromAddress} title="Company Address" />
              </div>
            </div>
          </div>

          {/* Compliance & Settings */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Compliance & Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500">Auto Payment Reminders</label>
                <p className="text-sm text-gray-900">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    invoice.automaticPaymentReminders ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {invoice.automaticPaymentReminders ? 'Enabled' : 'Disabled'}
                  </span>
                </p>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-500">Send Invoice To</label>
                <p className="text-sm text-gray-900">{invoice.sendInvoiceMailTo || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Attached Documents */}
          {invoice.documents && Array.isArray(invoice.documents) && invoice.documents.length > 0 && (
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Attached Documents</h3>
              <div className="space-y-3">
                {invoice.documents.map((doc, index) => (
                  doc && doc.link ? (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-white rounded-md border border-gray-200"
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={20} className="text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {doc.documentType || 'Document'}
                          </p>
                          <p className="text-xs text-gray-500">{doc.link ? doc.link.split('/').pop() : 'N/A'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownloadDocument(doc.link)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                        title="Download document"
                      >
                        <Download size={16} />
                      </button>
                    </div>
                  ) : null
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <label className="font-medium text-gray-500">Created At</label>
                <p className="text-gray-900">{formatDate(invoice.createdAt)}</p>
              </div>
              <div>
                <label className="font-medium text-gray-500">Updated At</label>
                <p className="text-gray-900">{formatDate(invoice.updatedAt)}</p>
              </div>
              <div>
                <label className="font-medium text-gray-500">Created By</label>
                <p className="text-gray-900">{invoice.createdBy || 'N/A'}</p>
              </div>
              <div>
                <label className="font-medium text-gray-500">Updated By</label>
                <p className="text-gray-900">{invoice.updatedBy || 'N/A'}</p>
              </div>
              {invoice.dueDate && (
                <div>
                  <label className="font-medium text-gray-500">Due Date</label>
                  <p className="text-gray-900">{formatDate(invoice.dueDate)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
          >
            Close
          </button>
        </div>
      </div>

      {/* Add animation styles */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default InvoicePreview;